import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

const MobileApp = () => {
  const [view, setView] = useState('projects'); // projects, files, code, ai
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [currentFile, setCurrentFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiResponse, setAiResponse] = useState('');

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Load files when project changes
  useEffect(() => {
    if (currentProject) {
      loadFiles(currentProject.id);
    }
  }, [currentProject]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await apiService.getProjects();
      setProjects(data);
      if (data.length > 0 && !currentProject) {
        setCurrentProject(data[0]);
      }
    } catch (err) {
      setError('Failed to load projects: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async (projectId) => {
    try {
      setLoading(true);
      const data = await apiService.getFiles(projectId);
      setFiles(data);
    } catch (err) {
      setError('Failed to load files: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectFile = async (file) => {
    try {
      setCurrentFile(file);
      setLoading(true);
      const fileData = await apiService.getFile(file.id);
      setFileContent(fileData.content || '');
      setView('code');
    } catch (err) {
      setError('Failed to load file: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveFile = async () => {
    if (!currentFile) return;
    try {
      setLoading(true);
      await apiService.updateFile(currentFile.id, { content: fileContent });
      alert('File saved!');
    } catch (err) {
      setError('Failed to save file: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const askAI = async () => {
    if (!aiInput.trim()) return;
    try {
      setLoading(true);
      const response = await apiService.generateWithAI({
        task_type: 'code_generation',
        prompt: aiInput,
        language: 'javascript',
        context: currentFile ? fileContent : ''
      });
      setAiResponse(response.result);
    } catch (err) {
      setError('AI request failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const importGitHub = async () => {
    const url = prompt('Enter GitHub repository URL:');
    if (!url) return;

    try {
      setLoading(true);
      const response = await apiService.importGitHubProject({
        github_url: url,
        project_name: url.split('/').pop().replace('.git', '')
      });
      alert('Repository imported! This may take a few minutes...');
      await loadProjects();
    } catch (err) {
      setError('Import failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Projects View
  const ProjectsView = () => (
    <div className="flex-1 overflow-y-auto pb-20 bg-gray-900">
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Projects</h2>
          <button
            onClick={importGitHub}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium active:bg-blue-700"
          >
            + Import
          </button>
        </div>

        {projects.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 mb-4">No projects yet</p>
            <button
              onClick={importGitHub}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium active:bg-blue-700"
            >
              Import from GitHub
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {projects.map(project => (
              <div
                key={project.id}
                onClick={() => {
                  setCurrentProject(project);
                  setView('files');
                }}
                className={`p-4 rounded-lg cursor-pointer active:scale-98 transition-all ${
                  currentProject?.id === project.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-300 active:bg-gray-700'
                }`}
              >
                <div className="font-medium">{project.name}</div>
                <div className="text-sm opacity-75 mt-1">
                  {project.github_url || 'Local project'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Files View
  const FilesView = () => (
    <div className="flex-1 overflow-y-auto pb-20 bg-gray-900">
      <div className="p-4">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => setView('projects')}
            className="p-2 text-gray-400 active:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 className="text-xl font-bold text-white flex-1">
            {currentProject?.name || 'Files'}
          </h2>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : files.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            No files in this project
          </div>
        ) : (
          <div className="space-y-1">
            {files.map(file => (
              <div
                key={file.id}
                onClick={() => selectFile(file)}
                className="p-3 rounded-lg bg-gray-800 active:bg-gray-700 cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate">{file.name}</div>
                    <div className="text-xs text-gray-400 truncate">{file.path}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Code Editor View
  const CodeView = () => (
    <div className="flex-1 flex flex-col bg-gray-900">
      <div className="p-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setView('files')}
            className="p-2 text-gray-400 active:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-white font-medium truncate">{currentFile?.name}</div>
            <div className="text-xs text-gray-400 truncate">{currentFile?.path}</div>
          </div>
          <button
            onClick={saveFile}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium active:bg-blue-700"
          >
            Save
          </button>
        </div>
      </div>

      <textarea
        value={fileContent}
        onChange={(e) => setFileContent(e.target.value)}
        className="flex-1 w-full p-4 bg-gray-900 text-gray-100 font-mono text-sm resize-none focus:outline-none"
        style={{ fontFamily: 'Consolas, Monaco, "Courier New", monospace' }}
        placeholder="File content..."
      />
    </div>
  );

  // AI View
  const AIView = () => (
    <div className="flex-1 flex flex-col pb-20 bg-gray-900">
      <div className="p-4 border-b border-gray-800">
        <h2 className="text-xl font-bold text-white">AI Assistant</h2>
        <p className="text-sm text-gray-400 mt-1">Ask for help with your code</p>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {aiResponse && (
          <div className="mb-4 p-4 bg-gray-800 rounded-lg">
            <div className="text-sm font-medium text-blue-400 mb-2">AI Response:</div>
            <div className="text-gray-300 whitespace-pre-wrap">{aiResponse}</div>
          </div>
        )}
      </div>

      <div className="p-4 bg-gray-800 border-t border-gray-700">
        <textarea
          value={aiInput}
          onChange={(e) => setAiInput(e.target.value)}
          placeholder="Ask AI for help..."
          className="w-full p-3 bg-gray-900 text-white rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-600 mb-3"
          rows="3"
        />
        <button
          onClick={askAI}
          disabled={loading || !aiInput.trim()}
          className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium active:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Thinking...' : 'Ask AI'}
        </button>
      </div>
    </div>
  );

  // Bottom Navigation
  const BottomNav = () => (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 safe-area-inset-bottom">
      <div className="flex justify-around items-center h-16">
        {[
          { id: 'projects', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', label: 'Projects' },
          { id: 'files', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z', label: 'Files' },
          { id: 'code', icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4', label: 'Code' },
          { id: 'ai', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z', label: 'AI' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setView(tab.id)}
            className={`flex flex-col items-center justify-center flex-1 h-full ${
              view === tab.id ? 'text-blue-400' : 'text-gray-400'
            }`}
          >
            <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
            </svg>
            <span className="text-xs">{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // Error Toast
  const ErrorToast = () => error && (
    <div className="fixed top-4 left-4 right-4 p-4 bg-red-600 text-white rounded-lg shadow-lg z-50 animate-slide-down">
      <div className="flex items-start gap-3">
        <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="flex-1">{error}</div>
        <button onClick={() => setError(null)} className="text-white">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-screen w-screen flex flex-col bg-gray-900 text-white overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 shadow-lg safe-area-inset-top">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Code Wizard</h1>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${loading ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`} />
          </div>
        </div>
      </div>

      {/* Content */}
      {view === 'projects' && <ProjectsView />}
      {view === 'files' && <FilesView />}
      {view === 'code' && <CodeView />}
      {view === 'ai' && <AIView />}

      {/* Bottom Navigation */}
      <BottomNav />

      {/* Error Toast */}
      <ErrorToast />

      <style jsx>{`
        @keyframes slide-down {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
        .safe-area-inset-top {
          padding-top: max(1rem, env(safe-area-inset-top));
        }
        .safe-area-inset-bottom {
          padding-bottom: env(safe-area-inset-bottom);
        }
        .active\\:scale-98:active {
          transform: scale(0.98);
        }
      `}</style>
    </div>
  );
};

export default MobileApp;
